import type { SearchProductApi } from './types.ts';

export function useSearchProductApi(): SearchProductApi {
  const loading = ref(false);

  // Default parameters for the methods
  const defaultPageable = {
    page: 0,
    size: 20,
    sort: ['name|ASC'],
  };
  const defaultFilters = {
    currency: 'EUR',
    locale: 'FR',
    brand: [] as string[],
    attributes: [] as string[],
    productTags: [] as string[],
    suppliers: [] as string[],
    aggregation: AggregationType.PRODUCT as AggregationType | undefined,
  };
  const defaultLocale = 'FR';

  /**
   * Search for products with a search term
   * @param search - The search string
   * @param locale - The locale in which the search is performed
   * @param pageable - Pagination options
   */

  const autoCompleteSearchProducts = async ({
    input,
    locale,
    currency,
    pageable,
    aggregation,
    productTags,
  }: AutocompleteProductsRequest) => {
    try {
      // Set loading to true
      loading.value = true;

      const { data, status, error } = await useFetch('/api/search/autocomplete', {
        params: {
          input,
          locale,
          currency,
          pageable: JSON.stringify(pageable),
          aggregation,
          productTags: JSON.stringify(productTags),
        },
        key: `autocomplete-${input}-${locale}-${currency}-${JSON.stringify(pageable)}-${aggregation}`,
      });

      // Sync loading state with the request's status
      loading.value = status.value === 'pending';

      // Handle errors
      if (error.value) {
        console.error('Error fetching autocomplete products:', error.value);
        return [];
      }

      // Return the products data
      return data.value || [];
    } catch (error) {
      console.error('Error in autoCompleteSearchProducts:', error);
      return [];
    } finally {
      // Ensure loading is reset
      loading.value = false;
    }
  };

  /**
   * Get a list of products with filters
   * @param filters - Filters to apply
   * @param locale - The locale in which the products are fetched
   * @param pageable - Pagination options
   * @param includeFacets - includeFacets Option
   */
  const getProductsList = async ({
    locale = defaultLocale,
    filters = defaultFilters,
    pageable = defaultPageable,
    includeFacets = true,
  }: SearchProductsRequest) => {
    try {
      loading.value = true;

      const { data, status, error } = await useFetch('/api/search/products', {
        params: {
          locale,
          filters: JSON.stringify(filters),
          pageable: JSON.stringify(pageable),
          includeFacets: includeFacets,
        },
        key: `products-${locale}-${JSON.stringify(filters)}-${JSON.stringify(pageable)}-${includeFacets}`,
      });

      loading.value = status.value === 'pending';

      if (error.value) {
        console.error('Error fetching products:', error.value);
        return {} as SearchProductsResponse;
      }
      return data.value;
    } catch (error) {
      console.error('Error in getProductsList:', error);
      return {} as SearchProductsResponse;
    } finally {
      loading.value = false; // Ensure loading is reset
    }
  };

  return {
    autoCompleteSearchProducts,
    getProductsList,
  };
}
